/**
 * Discount Popup - Cart-integrated discount tier system
 */

class DiscountPopup {
  constructor() {
    this.trigger = document.querySelector('[data-discount-popup-trigger]');
    this.modal = document.querySelector('[data-discount-popup-modal]');
    this.closeButtons = document.querySelectorAll('[data-discount-popup-close]');
    this.isOpen = false;
    this.cartTotal = 0;
    this.tierData = [];
    this.cartUpdateUnsubscriber = undefined;
    
    if (!this.trigger || !this.modal) return;
    
    this.init();
  }
  
  init() {
    this.extractTierData();
    this.updateCartData();
    this.updateAllTiers();
    this.updateTriggerButtonTiers();
    this.bindEvents();
    this.subscribeToCartUpdates();
  }
  
  bindEvents() {
    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.openPopup();
    });
    
    this.closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.closePopup();
      });
    });
    
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal || e.target.classList.contains('discount-popup__overlay')) {
        this.closePopup();
      }
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closePopup();
      }
    });
    
    this.modal.addEventListener('transitionend', () => {
      document.body.style.overflow = this.isOpen ? 'hidden' : '';
    });

    setInterval(() => {
      this.trigger.classList.toggle('shake');
    }, 1500);
  }
  
  subscribeToCartUpdates() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, this.refreshPopup.bind(this));
  }
  
  handleCartUpdate(event) {
    if (!event?.cartData) return;
    
    const newCartTotal = event.cartData.total_price ? event.cartData.total_price / 100 : 0;
    
    if (Math.abs(newCartTotal - this.cartTotal) > 0.01) {
      this.cartTotal = newCartTotal;
      this.updateAllTiers();
      this.updateCartTotalDisplay();
      this.updateDataAttributes();
    }
  }
  
  extractTierData() {
    const tierRows = this.modal.querySelectorAll('.discount-popup__tier-row');
    this.tierData = [];
    
    tierRows.forEach((row, index) => {
      const discountLabel = row.querySelector('.discount-popup__tier-badge-text')?.textContent || '';
      const tierAmount = parseFloat(row.dataset.tierAmount);
      
      if (!isNaN(tierAmount)) {
        this.tierData.push({
          index,
          amount: tierAmount,
          discountLabel,
          element: row
        });
      }
    });
    
    this.tierData.sort((a, b) => a.amount - b.amount);
    this.extractTriggerTierData();
  }
  
  extractTriggerTierData() {
    const triggerTiers = this.trigger.querySelectorAll('.discount-popup__tier');
    this.triggerTierData = [];
    
    triggerTiers.forEach((tier, index) => {
      const tierAmount = parseFloat(tier.dataset.triggerTierAmount);
      const discountLabel = tier.querySelector('.discount-popup__tier-text')?.textContent || '';
      
      if (!isNaN(tierAmount)) {
        this.triggerTierData.push({
          index,
          amount: tierAmount,
          discountLabel,
          element: tier
        });
      }
    });
    
    this.triggerTierData.sort((a, b) => a.amount - b.amount);
  }
  
  updateCartData() {
    const container = this.trigger.closest('.discount-popup');
    if (container) {
      this.cartTotal = parseFloat(container.dataset.cartTotal) || 0;
    }
  }
  
  async refreshPopup() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        const newCartTotal = cart.total_price / 100;
        
        if (Math.abs(newCartTotal - this.cartTotal) > 0.01) {
          this.cartTotal = newCartTotal;
          this.updateAllTiers();
          this.updateCartTotalDisplay();
          this.updateDataAttributes();
        }
      }
    } catch (error) {
      console.warn('Failed to refresh popup:', error);
    }
  }
  
  updateAllTiers() {
    if (this.tierData.length === 0) {
      this.extractTierData();
    }
    
    let lastAchievedTier = null;
    let firstUnachievedTier = null;
    
    this.tierData.forEach(tier => {
      const isAchieved = this.cartTotal >= tier.amount;
      
      if (isAchieved) {
        tier.element.classList.add('discount-popup__tier-row--achieved');
        lastAchievedTier = tier;
      } else {
        tier.element.classList.remove('discount-popup__tier-row--achieved');
        if (!firstUnachievedTier) {
          firstUnachievedTier = tier;
        }
      }
      
      this.updateTierIndicator(tier, isAchieved);
      this.updateTierBadge(tier, isAchieved);
    });
    
    this.updateProgressMessage(lastAchievedTier, firstUnachievedTier);
    this.updateTriggerButtonTiers();
  }
  
  updateTierIndicator(tier, isAchieved) {
    const tierIndicator = tier.element.querySelector('.discount-popup__tier-indicator');
    const svgIcon = tierIndicator?.querySelector('.discount-popup__tier-indicator-icon');
    
    if (tierIndicator && svgIcon) {
      svgIcon.style.display = isAchieved ? 'block' : 'none';
      tierIndicator.classList.toggle('discount-popup__tier-indicator--achieved', isAchieved);
    }
  }
  
  updateTierBadge(tier, isAchieved) {
    const badge = tier.element.querySelector('.discount-popup__tier-badge');
    if (badge) {
      badge.classList.toggle('discount-popup__tier-badge--achieved', isAchieved);
    }
  }
  
  updateTriggerButtonTiers() {
    const tiers = this.trigger.querySelectorAll('.discount-popup__tier');
    
    tiers.forEach(tierElement => {
      const tierAmount = parseFloat(tierElement.dataset.triggerTierAmount);
      if (!isNaN(tierAmount)) {
        const isAchieved = this.cartTotal >= tierAmount;
        tierElement.classList.toggle('discount-popup__tier--achieved', isAchieved);
      }
    });
  }
  
  updateCartTotalDisplay() {
    const cartTotalElement = this.modal.querySelector('.discount-popup__cart-total-amount');
    if (cartTotalElement) {
      cartTotalElement.textContent = `€${this.cartTotal.toFixed(2)}`;
    }
  }
  
  updateDataAttributes() {
    const container = this.trigger.closest('.discount-popup');
    if (container) {
      container.dataset.cartTotal = this.cartTotal.toString();
      const achievedTiers = this.tierData.filter(tier => this.cartTotal >= tier.amount).length;
      container.dataset.achievedTiers = achievedTiers.toString();
    }
  }
  
  updateProgressMessage(lastAchievedTier, firstUnachievedTier) {
    const allProgressMessages = this.modal.querySelectorAll('.discount-popup__progress-message');
    allProgressMessages.forEach(msg => msg.style.display = 'none');
    
    if (lastAchievedTier && firstUnachievedTier) {
      const lastAchievedElement = lastAchievedTier.element;
      const nextElement = lastAchievedElement.nextElementSibling;
      
      if (nextElement?.classList.contains('discount-popup__progress-message')) {
        const amountNeeded = firstUnachievedTier.amount - this.cartTotal;
        const amountElement = nextElement.querySelector('.discount-popup__progress-message-amount');
        
        if (amountElement) {
          amountElement.textContent = `€${amountNeeded.toFixed(2).replace('.', ',')}`;
        }
        
        nextElement.style.display = 'flex';
      }
    }
  }
  
  openPopup() {
    this.modal.classList.add('discount-popup__modal--active');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    
    const firstFocusableElement = this.modal.querySelector('.discount-popup__close');
    if (firstFocusableElement) firstFocusableElement.focus();
    
    // Only prevent body scroll on mobile
    if (window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
    }
    
    this.announceToScreenReader('Discount popup opened');
  }
  
  closePopup() {
    this.modal.classList.remove('discount-popup__modal--active');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
    
    // Only restore body scroll on mobile
    if (window.innerWidth <= 768) {
      document.body.style.overflow = '';
    }
    
    this.trigger.focus();
    this.announceToScreenReader('Discount popup closed');
  }
  
  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  }
  
  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = undefined;
    }
  }
}

// Initialize
const initDiscountPopup = () => {
  new DiscountPopup();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDiscountPopup);
} else {
  initDiscountPopup();
}

// Export for external use
window.DiscountPopup = DiscountPopup;